import React, { ReactNode, useRef } from 'react';
import { propsToDataAttributes } from '@tr-digital/react-rehydrate';
import './index.css';
import {
  Dropdown,
  FlexGrid,
  getClassNameFactory,
  getRehydratableName,
  Heading,
  Rank,
  Typography,
} from '@dcl/ui';
import { ITitleWithSelector } from './types';
import { TypographyTags } from '@dcl/ui/dist/elements/Typography';
import { types } from '@dcl/ui/dist/elements/Heading';

import ReactHtmlParser from 'react-html-parser';
import { IDropdownItem } from '@dcl/ui/dist/modules/Dropdown';
import { mockTextColor, mockTextColorBlack } from './mock';

export const TitleWithSelector: React.FC<ITitleWithSelector> = (props) => {
  const {
    bannerTitle,
    bannerDescription,
    dropdownLabel,
    dropDownItems,
    textColor = mockTextColor,
  } = props;
  const getClassName = getClassNameFactory(TitleWithSelector.displayName);
  const dropdownRef = useRef<Dropdown>();

  const handleValueChange = (value: string) => {
    window.location.href = value;
  };

  const filterFn = (
    items: IDropdownItem[],
    value: ReactNode
  ): IDropdownItem[] => {
    const label = (value?.toString() ?? '').toLowerCase();

    return items.filter((item) => item.label.toLowerCase().includes(label));
  };

  function capitalizeFirstLetter(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  const formattedDropDownItems = [...(dropDownItems ?? [])]
    .sort((itemA, itemB) => {
      const labelA = itemA.linkText.toLowerCase();
      const labelB = itemB.linkText.toLowerCase();
      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    })
    .map((item) => ({
      label: capitalizeFirstLetter(item.linkText),
      value: item.linkPath,
    }));

  const getTextColorClass = (baseClass: string) => {
    if (textColor === mockTextColorBlack) {
      return `${getClassName({ descendantName: baseClass })} ${getClassName({
        descendantName: 'blackHeading',
      })}`;
    }
    return getClassName({ descendantName: baseClass });
  };

  return (
    <div
      data-rehydratable={getRehydratableName(TitleWithSelector.displayName)}
      className={getClassName()}
      {...propsToDataAttributes(props)}
    >
      <FlexGrid fullWidth>
        <FlexGrid.Row>
          <FlexGrid.Col xs={12} lg={9} md={8}>
            {bannerTitle && bannerTitle !== '' && (
              <Heading
                rank={Rank.First}
                className={getTextColorClass('mainHeading')}
                type={types.Large}
              >
                {bannerTitle}
              </Heading>
            )}
            {bannerDescription && bannerDescription !== '' && (
              <Typography
                tag={TypographyTags.P}
                className={getTextColorClass('description')}
              >
                {ReactHtmlParser(bannerDescription)}
              </Typography>
            )}
          </FlexGrid.Col>

          {dropDownItems && dropDownItems.length > 0 && (
            <FlexGrid.Col
              xs={12}
              lg={3}
              md={8}
              className={getClassName({ descendantName: 'dropDown' })}
            >
              <FlexGrid.Row>
                <FlexGrid.Col xs={12} lg={12} md={12}>
                  <label htmlFor={`dropdown-${dropdownLabel}`}>
                    <Typography
                      tag={TypographyTags.P}
                      className={getTextColorClass('dropDownLabel')}
                    >
                      {dropdownLabel}
                    </Typography>
                  </label>
                  <Dropdown
                    ref={(el) => (dropdownRef.current = el!)}
                    filterFunction={filterFn}
                    typeaheadMode
                    maxVisibleItems={5}
                    block={true}
                    items={formattedDropDownItems}
                    labeledBy={dropdownLabel}
                    placeholder=""
                    onValueChanged={(value) =>
                      handleValueChange(value as string)
                    }
                    className={getTextColorClass('regionDropdown')}
                    removeAutoFocus
                  />
                </FlexGrid.Col>
              </FlexGrid.Row>
            </FlexGrid.Col>
          )}
        </FlexGrid.Row>
      </FlexGrid>
    </div>
  );
};

TitleWithSelector.displayName = 'TitleWithSelector';
